import React from 'react';
import { type ColumnDef } from '@/components/organism/Table';
import { Cell, PillCell } from '@/components/atoms';
import { getDateTimeAgo, getDateTime } from '@/utils/dateTime';
import { AlertLevelIcon, LEVEL_COLOR } from '@/pages/Alert/AlertLevelIcon';
import { ALERTS_PROPS } from '@/constant/alerts/alerts.constant';
import { tid } from '@/utils/testId';
import { ALERT_TEST_IDS as TID } from '@/constant/testIds/alert.testIds';
import { IAlertProps } from './alerts.interface';

// ─── Column definitions ───────────────────────────────────────────────────────
export const buildColumns = (
  isCopy: string,
  onCopyClick: (key: string) => void,
): ColumnDef<IAlertProps>[] => [
    {
      accessor: 'operatorId',
      header: ALERTS_PROPS.COLUMNS.OPERATOR_ID,
      render: (item, idx) => (
        <Cell
          testId={tid(TID.COL_OPERATOR_ID(`${idx}`))}
          value={item?.operatorId}
          tooltip
          variant="primary"
          copy={{ copyKey: `operatorId-${item?.alertId}`, isCopy, onCopyClick }}
          className="w-[120px]"
        />
      ),
    },
    {
      accessor: 'alertId',
      header: ALERTS_PROPS.COLUMNS.ALERT_ID,
      render: (item, idx) => (
        <Cell
          testId={tid(TID.COL_ALERT_ID(`${idx}`))}
          value={item?.alertId}
          tooltip
          variant="primary"
          copy={{ copyKey: `alertId-${item?.alertId}`, isCopy, onCopyClick }}
          className="w-[240px]"
        />
      ),
    },
    {
      accessor: 'marketId',
      header: ALERTS_PROPS.COLUMNS.MARKET_ID,
      render: (item, idx) => (
        <Cell
          testId={tid(TID.COL_MARKET_ID(`${idx}`))}
          value={item?.marketId}
          className="w-[120px]"
        />
      ),
    },
    {
      accessor: 'deviceId',
      header: ALERTS_PROPS.COLUMNS.DEVICE_ID,
      render: (item, idx) => (
        <Cell
          testId={tid(TID.COL_DEVICE_ID(`${idx}`))}
          value={item?.deviceId}
          className="w-[120px]"
        />
      ),
    },
    {
      accessor: 'type',
      header: ALERTS_PROPS.COLUMNS.TYPE,
      render: (item, idx) => (
        <Cell testId={tid(TID.COL_TYPE(`${idx}`))} value={item?.type} />
      ),
    },
    {
      accessor: 'statusCode',
      header: ALERTS_PROPS.COLUMNS.STATUS_CODE,
      render: (item, idx) => (
        <PillCell
          testId={tid(TID.COL_STATUS_CODE(`${idx}`))}
          value={item?.statusCode}
          preset="statusCode"
          solid
          className="min-w-[3.5rem] justify-center tabular-nums"
        />
      ),
    },
    {
      accessor: 'level',
      header: ALERTS_PROPS.COLUMNS.LEVEL,
      render: (item, idx) => {
        const lvl = String(item?.level ?? '').toUpperCase();
        const color = LEVEL_COLOR[lvl];
        return (
          <PillCell
            testId={tid(TID.COL_LEVEL(`${idx}`))}
            value={item?.level}
            icon={<AlertLevelIcon level={item?.level} className="w-3 h-3" />}
            customStyle={
              color ? { backgroundColor: `${color}18`, color } : undefined
            }
            config={
              color
                ? { bg: '', text: '' }
                : { bg: 'bg-gray-100', text: 'text-gray-500' }
            }
          />
        );
      },
    },
    {
      accessor: 'status',
      header: ALERTS_PROPS.COLUMNS.STATUS,
      render: (item, idx) => (
        <PillCell
          testId={tid(TID.COL_STATUS(`${idx}`))}
          value={item?.status}
          preset="alertStatus"
          dot
        />
      ),
    },
    {
      accessor: 'triggeredOn',
      header: ALERTS_PROPS.COLUMNS.TRIGGERED_ON,
      sorting: true,
      render: (item, idx) => (
        <Cell
          testId={tid(TID.COL_TRIGGERED_ON(`${idx}`))}
          value={item?.triggeredOn}
          format="datetime"
          variant="muted"
        />
      ),
    },
    {
      accessor: 'closedOn',
      header: ALERTS_PROPS.COLUMNS.CLOSED_ON,
      sorting: true,
      render: (item, idx) => (
        <Cell
          testId={tid(TID.COL_CLOSED_ON(`${idx}`))}
          value={item?.closedOn}
          variant="muted"
          tooltip={item?.closedOn ? getDateTime(item.closedOn) : undefined}
          tooltipPlacement="top"
        >
          {item?.closedOn ? getDateTimeAgo(item.closedOn) : '—'}
        </Cell>
      ),
    },
  ];
